import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  Select,
  StackLayout,
  UtilityText,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import {
  formatDate,
  formatPhoneNumber,
  isEmpty,
  isNoSpace,
  isNotEmpty,
  isValidZipCodeLength,
  isValidatePhoneLength,
  phoneNumberOption,
  stateOption,
  updateFormData,
} from '../../../helpers';

export function PolicyHolderInformation(props: any) {
  const {
    nextButtonClicked,
    errorHandling,
    profileResponseData,
    planDetailsResponseData,
    footerToBottom,
  } = props;
  const [relationship] = useState(
    profileResponseData?.demographics?.relationship,
  );
  const [zipCodeError, setZipCodeError] = useState<string>('');
  const [businessPhoneError, setBusinessPhoneError] = useState<string>('');
  const [homePhoneError, setHomePhoneError] = useState<string>('');

  const phoneNumber = phoneNumberOption(profileResponseData).filter(
    (opt: any) => opt.use === 'Home',
  )[0]?.value;

  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);

  const [form, setForm] = useState({
    memberName:
      relationship === 'Subscriber'
        ? `${profileResponseData?.demographics?.firstName} ${profileResponseData?.demographics?.lastName}`
        : '',
    memberId: profileResponseData?.demographics?.memberId,
    addressLine1:
      relationship === 'Subscriber'
        ? profileResponseData?.demographics?.address.line1
        : '',
    addressLine2:
      relationship === 'Subscriber'
        ? profileResponseData?.demographics?.address.line2
        : '',
    city:
      relationship === 'Subscriber'
        ? profileResponseData?.demographics?.address.city
        : '',
    state:
      relationship === 'Subscriber'
        ? profileResponseData?.demographics?.address.state
        : '',
    zipCode:
      relationship === 'Subscriber'
        ? profileResponseData?.demographics?.address.postalCode
        : '',
    businessPhoneNumber:
      relationship === 'Subscriber'
        ? phoneNumberOption(profileResponseData).filter(
            (opt: any) => opt.use === 'Work',
          )[0]?.value
        : '',
    homePhoneNumber:
      relationship === 'Subscriber'
        ? phoneNumberOption(profileResponseData).filter(
            (opt: any) => opt.use === 'Home',
          )[0]?.value
        : '',
    dependents: [],
    relationship,
  });

  const [errors, setErrors] = useState({
    memberName: false,
    addressLine1: false,
    city: false,
    state: false,
    zipCode: false,
    businessPhoneNumber: false,
    homePhoneNumber: false,
  });

  const formField =
    isEmpty(form.memberName) ||
    isEmpty(form.addressLine1) ||
    isEmpty(form.city) ||
    isEmpty(form.state) ||
    isEmpty(form.zipCode) ||
    isValidZipCodeLength(form.zipCode) ||
    isEmpty(form.homePhoneNumber) ||
    isValidatePhoneLength(form.homePhoneNumber) ||
    isValidatePhoneLength(form.businessPhoneNumber);

  const isValid =
    (relationship === 'Subscriber' && isNotEmpty(form.homePhoneNumber)) ||
    (isNotEmpty(form.memberName) &&
      isNotEmpty(form.addressLine1) &&
      isNotEmpty(form.city) &&
      isNotEmpty(form.state) &&
      isNotEmpty(form.zipCode) &&
      !isValidZipCodeLength(form.zipCode) &&
      isNotEmpty(form.homePhoneNumber) &&
      !isValidatePhoneLength(form.homePhoneNumber));

  useEffect(() => {
    if (relationship === 'Subscriber') {
      if (isEmpty(form.homePhoneNumber)) {
        errorHandling(false);
        setHomePhoneError('Home phone number is required.');
      } else if (isValidatePhoneLength(form.homePhoneNumber)) {
        errorHandling(false);
        setHomePhoneError('Enter a valid phone number.');
      } else {
        errorHandling(true);
        setHomePhoneError('');
      }
    } else if (
      formField ||
      errors.zipCode ||
      errors.homePhoneNumber ||
      errors.businessPhoneNumber
    ) {
      errorHandling(false);

      if (isEmpty(form.zipCode)) {
        setZipCodeError('zip code is required.');
      } else if (isValidZipCodeLength(form.zipCode)) {
        setZipCodeError('Please enter a valid zip code.');
      } else {
        setZipCodeError('');
      }

      if (isEmpty(form.homePhoneNumber)) {
        setHomePhoneError('Home phone number is required.');
      } else if (isValidatePhoneLength(form.homePhoneNumber)) {
        setHomePhoneError('Enter a valid phone number.');
      } else {
        setHomePhoneError('');
      }

      if (isValidatePhoneLength(form.businessPhoneNumber)) {
        setBusinessPhoneError('Enter a valid phone number.');
      } else {
        setBusinessPhoneError('');
      }
    } else {
      errorHandling(true);
    }
  }, [formField, errorHandling, form, errors, relationship]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          memberName: isEmpty(form.memberName),
          addressLine1: isEmpty(form.addressLine1),
          city: isEmpty(form.city),
          state: isEmpty(form.state),
          zipCode: isEmpty(form.zipCode) || isValidZipCodeLength(form.zipCode),
          businessPhoneNumber: isNotEmpty(form.businessPhoneNumber)
            ? isValidatePhoneLength(form.businessPhoneNumber)
            : false,
          homePhoneNumber:
            isEmpty(form.homePhoneNumber) ||
            isValidatePhoneLength(form.homePhoneNumber),
        });
      }
    }
  }, [isValid, form, nextButtonClicked]);

  const updateErrorState = (value: string, id: string) => {
    if (id === 'zipCode') {
      if (isEmpty(value)) {
        setZipCodeError('zip code is required.');
        setErrors((prevState) => ({
          ...prevState,
          zipCode: true,
        }));
      } else if (isValidZipCodeLength(value)) {
        setZipCodeError('Please enter a valid zip code.');
        setErrors((prevState) => ({
          ...prevState,
          zipCode: true,
        }));
      } else {
        setZipCodeError('');
        setErrors((prevState) => ({
          ...prevState,
          zipCode: false,
        }));
      }
    } else if (id === 'homePhoneNumber') {
      if (isEmpty(value)) {
        setHomePhoneError(
          'Your phone number is required to process this request.',
        );
        setErrors((prevState) => ({
          ...prevState,
          homePhoneNumber: true,
        }));
      } else if (isValidatePhoneLength(value)) {
        setHomePhoneError('Enter a valid phone number.');
        setErrors((prevState) => ({
          ...prevState,
          homePhoneNumber: true,
        }));
      } else {
        setHomePhoneError('');
        setErrors((prevState) => ({
          ...prevState,
          homePhoneNumber: false,
        }));
      }
    } else if (id === 'businessPhoneNumber') {
      if (isValidatePhoneLength(value)) {
        setBusinessPhoneError('Enter a valid phone number.');
        setErrors((prevState) => ({
          ...prevState,
          businessPhoneNumber: true,
        }));
      } else {
        setBusinessPhoneError('');
        setErrors((prevState) => ({
          ...prevState,
          businessPhoneNumber: false,
        }));
      }
    } else {
      const nextErrorState = {
        ...errors,
        [id]: isEmpty(value),
      };
      setErrors(nextErrorState);
    }
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;

    const nextFormState = {
      ...form,
      [id]: value,
    };
    setForm(nextFormState);
    if (
      id !== 'addressLine2' &&
      id !== 'homePhoneNumber' &&
      id !== 'businessPhoneNumber'
    ) {
      updateErrorState(value, id);
    }

    if (id === 'memberName') {
      setForm((prevState) => ({
        ...prevState,
        memberName: isNoSpace(value)
          ? ''
          : value?.replace(/[^a-zA-Z0-9 \-']+/gi, ''),
      }));
    }

    if (id === 'city') {
      setForm((prevState) => ({
        ...prevState,
        city: isNoSpace(value) ? '' : value?.replace(/[^a-zA-Z ]+/gi, ''),
      }));
    }

    if (id === 'zipCode') {
      setForm((prevState) => ({
        ...prevState,
        zipCode: isNoSpace(value) ? '' : value?.replace(/[^0-9]+/gi, ''),
      }));
    }

    if (id === 'homePhoneNumber') {
      setForm((prevState) => ({
        ...prevState,
        homePhoneNumber: isNoSpace(value) ? '' : formatPhoneNumber(value),
      }));
      updateErrorState(formatPhoneNumber(value), id);
    }

    if (id === 'businessPhoneNumber') {
      setForm((prevState) => ({
        ...prevState,
        businessPhoneNumber: isNoSpace(value) ? '' : formatPhoneNumber(value),
      }));
      updateErrorState(formatPhoneNumber(value), id);
    }
  };

  useEffect(() => {
    const pInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    const dependents = profileResponseData?.demographics?.dependents;
    const members = planDetailsResponseData?.plan?.members;
    if (relationship === 'Subscriber') {
      if (dependents !== null && dependents.length > 0) {
        if (members) {
          const memberNames = members.map(
            (mem: any) => `${mem.firstName} ${mem.lastName}`,
          );
          const dependentsData = dependents.filter(
            (dep: any) =>
              memberNames.indexOf(`${dep.firstName} ${dep.lastName}`) > -1,
          );
          const dependentsUser = dependentsData.map((option: any) => ({
            name: `${option.firstName} ${option.lastName}`,
            dateOfBirth: formatDate(option.dateOfBirth),
            relationship: `${option.relationship}`,
          }));
          if (dependentsUser.length > 0) {
            setForm((prevState) => ({
              ...prevState,
              dependents: dependentsUser,
              isPolicyHolderPatient: pInfo?.isPolicyHolderPatient || '',
              patientRelationship: pInfo?.patientRelationship || '',
              patientName: pInfo?.patientName || '',
              patientDateOfBirth: pInfo?.patientDateOfBirth || '',
              homePhoneNumber:
                pInfo?.homePhoneNumber ||
                phoneNumberOption(profileResponseData).filter(
                  (opt: any) => opt.use === 'Home',
                )[0]?.value,
            }));
          } else {
            setForm((prevState) => ({
              ...prevState,
              dependents: dependentsUser,
              isPolicyHolderPatient: 'YES',
              homePhoneNumber:
                pInfo?.homePhoneNumber ||
                phoneNumberOption(profileResponseData).filter(
                  (opt: any) => opt.use === 'Home',
                )[0]?.value,
            }));
          }
        } else {
          setForm((prevState) => ({
            ...prevState,
            dependents: [],
            isPolicyHolderPatient: 'YES',
            homePhoneNumber:
              pInfo?.homePhoneNumber ||
              phoneNumberOption(profileResponseData).filter(
                (opt: any) => opt.use === 'Home',
              )[0]?.value,
          }));
        }
      } else {
        setForm((prevState) => ({
          ...prevState,
          dependents: [],
          isPolicyHolderPatient: 'YES',
          homePhoneNumber:
            pInfo?.homePhoneNumber ||
            phoneNumberOption(profileResponseData).filter(
              (opt: any) => opt.use === 'Home',
            )[0]?.value,
        }));
      }
    } else {
      const dependentsUser = members.map((option: any) => ({
        name: `${option.firstName} ${option.lastName}`,
        relationship,
      }));
      setForm((prevState) => ({
        ...prevState,
        memberName: pInfo?.memberName || '',
        memberId:
          pInfo?.memberId || profileResponseData?.demographics?.memberId,
        addressLine1: pInfo?.addressLine1 || '',
        addressLine2: pInfo?.addressLine2 || '',
        city: pInfo?.city || '',
        state: pInfo?.state || '',
        zipCode: pInfo?.zipCode || '',
        homePhoneNumber: pInfo?.homePhoneNumber || '',
        businessPhoneNumber: pInfo?.businessPhoneNumber || '',
        relationship: pInfo?.relationship,
        dependents: dependentsUser,
        isPolicyHolderPatient: 'NO',
        patientRelationship: relationship,
        patientName: `${profileResponseData?.demographics?.firstName} ${profileResponseData?.demographics?.lastName}`,
        patientDateOfBirth: formatDate(
          profileResponseData?.demographics?.dateOfBirth,
        ),
      }));
    }
  }, [planDetailsResponseData, profileResponseData, relationship]);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
      horizontalAlignment="center"
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $two $two $two',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$none $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="1"
            size={{
              '@initial': 'xxl',
              '@mobile': 'xl',
              '@mobileLandscape': 'xl',
              '@tablet': 'xl',
              '@laptop': 'xxl',
              '@desktop': 'xxl',
            }}
          >
            Direct Reimbursement Claim Form
          </HeadingText>
        </Box>

        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $none',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                backgroundColor: '#F8F8F8',
                padding: '$half $two $one $two',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Form Instructions:
            </HeadingText>
            <Box
              css={
                {
                  marginTop: '$one',
                  paddingLeft: '$half',
                } as GDSStyleObject
              }
            >
              <UtilityText>
                <ol>
                  <li key="1">
                    1. Use a separate claim form for each patient.
                  </li>
                  <li key="2">
                    2. Expenses for both{' '}
                    <span style={{ fontWeight: 'bold' }}>examinations</span> and{' '}
                    <span style={{ fontWeight: 'bold' }}>eyewear</span> can be
                    claimed on this form. Only services listed on this form will
                    be considered for reimbursement.
                  </li>
                  <li key="3">
                    3. Attach the receipts or bills for the services claimed.
                  </li>
                  <li key="4">
                    4. Enter the full name, address and zip code when a name and
                    address field is required.
                  </li>
                  <li key="5">
                    5. All fields are required unless listed as optional.
                  </li>
                </ol>
              </UtilityText>
            </Box>
          </Box>
        </Box>
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $two',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Step 1: Policy Holder Information
            </HeadingText>
            <Box
              css={
                {
                  marginTop: '$one',
                  paddingLeft: '$one',
                } as GDSStyleObject
              }
            >
              <ParagraphText
                css={
                  {
                    marginTop: '$one',
                  } as GDSStyleObject
                }
              >
                <p>
                  This is the policy holder&apos;s information on file. If you
                  need to make updates, you can change your contact information
                  in Account Settings. The information here will not affect your
                  claims submission.
                </p>
              </ParagraphText>
            </Box>
          </Box>
          <Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="memberName"
                label="Member Name"
                statusIconLabel="Error"
                inputStatus={errors.memberName ? 'error' : undefined}
                statusMessage={
                  errors.memberName ? "Member's name is required." : ''
                }
              >
                <TextInput
                  id="memberName"
                  disabled={form.relationship === 'Subscriber'}
                  onChange={onUpdateField}
                  value={form.memberName}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="memberId" label="Member ID">
                <TextInput disabled value={form.memberId} />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="addressLine1"
                label="Member Address Line 1"
                statusIconLabel="Error"
                inputStatus={errors.addressLine1 ? 'error' : undefined}
                statusMessage={
                  errors.addressLine1 ? 'Address line 1 is required.' : ''
                }
              >
                <TextInput
                  id="addressLine1"
                  disabled={form.relationship === 'Subscriber'}
                  onChange={onUpdateField}
                  value={form.addressLine1}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="addressLine2"
                label="Member Address Line 2 (Optional)"
              >
                <TextInput
                  id="addressLine2"
                  disabled={form.relationship === 'Subscriber'}
                  onChange={onUpdateField}
                  value={form.addressLine2}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="city"
                label="City"
                statusIconLabel="Error"
                inputStatus={errors.city ? 'error' : undefined}
                statusMessage={errors.city ? 'City is required.' : ''}
              >
                <TextInput
                  id="city"
                  disabled={form.relationship === 'Subscriber'}
                  onChange={onUpdateField}
                  value={form.city}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="state"
                label="State"
                statusIconLabel="Error"
                inputStatus={errors.state ? 'error' : undefined}
                statusMessage={errors.state ? 'State is required.' : ''}
              >
                {form.relationship === 'Subscriber' ? (
                  <TextInput value={form.state} disabled />
                ) : (
                  <Select
                    name="state"
                    options={stateOption()}
                    placeholder="Please Select"
                    id="state"
                    value={form.state}
                    onChange={onUpdateField}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                )}
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="zipCode"
                label="Zip Code"
                statusIconLabel="Error"
                inputStatus={errors.zipCode ? 'error' : undefined}
                statusMessage={errors.zipCode ? zipCodeError : ''}
              >
                <TextInput
                  id="zipCode"
                  disabled={form.relationship === 'Subscriber'}
                  maxLength={5}
                  onChange={onUpdateField}
                  value={form.zipCode}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="businessPhoneNumber"
                label="Business Phone Number (Optional)"
                statusIconLabel="Error"
                inputStatus={errors.businessPhoneNumber ? 'error' : undefined}
                statusMessage={
                  errors.businessPhoneNumber ? businessPhoneError : ''
                }
              >
                <TextInput
                  id="businessPhoneNumber"
                  disabled={form.relationship === 'Subscriber'}
                  maxLength={14}
                  onChange={onUpdateField}
                  value={form.businessPhoneNumber}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="homePhoneNumber"
                label="Home Phone Number"
                statusIconLabel="Error"
                inputStatus={errors.homePhoneNumber ? 'error' : undefined}
                statusMessage={errors.homePhoneNumber ? homePhoneError : ''}
              >
                <TextInput
                  id="homePhoneNumber"
                  disabled={phoneNumber && form.relationship === 'Subscriber'}
                  maxLength={14}
                  onChange={onUpdateField}
                  value={form.homePhoneNumber}
                  onBlur={onBlurField}
                />
              </FormField>
            </Box>
          </Box>
        </Box>
      </Box>
    </StackLayout>
  );
}
